import { useState, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import b1 from "../assets/b1.jpeg";
import b2 from "../assets/b2.jpeg";
import b3 from "../assets/b3.jpeg";
import b4 from "../assets/b4.jpeg";
import b5 from "../assets/b5.jpeg";
import b6 from "../assets/b6.jpeg";
import { FaFlag, FaShieldAlt, FaTimes } from "react-icons/fa";
import p1 from "../assets/p1.jpeg";
import p2 from "../assets/p2.jpeg";
import p3 from "../assets/p3.jpeg";
import p4 from "../assets/p4.jpeg";
import p5 from "../assets/p5.jpeg";
import p6 from "../assets/p6.jpeg";
import p7 from "../assets/p7.jpeg";
import ph from "../assets/placeholder.jpeg";
import heroVideo from "../assets/bannerVideo.mp4";
// ─── 👇 Drop your mobile video in /assets and update this import
import heroVideoMobile from "../assets/mobileVideo.mp4";
import phVideo from "../assets/phVideo.mp4";
import Navbar from "./Navbar";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { width: 100%; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideTrack = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const backdropIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const AppContainer = styled.div`
  position: relative;
  width: 100vw;
  overflow: hidden;
  background: #0a0010;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        ellipse 80% 55% at 50% -5%,
        #7404be55 0%,
        transparent 70%
      ),
      radial-gradient(
        ellipse 60% 40% at 20% 100%,
        #a855fd33 0%,
        transparent 65%
      ),
      radial-gradient(ellipse 50% 35% at 80% 90%, #7404be22 0%, transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
`;

const Phone = styled.div`
  position: relative;
  width: 100%;
  max-width: ${({ theme }) => (theme.isDesktop ? "1400px" : "auto")};
  margin: 0 auto;
  background: #0a0010;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: ${({ theme }) =>
    theme.isDesktop ? "0 25px 50px -12px rgba(0,0,0,0.25)" : "none"};
  border-radius: ${({ theme }) => (theme.isDesktop ? "48px" : "0")};
  border: ${({ theme }) =>
    theme.isDesktop ? "1px solid rgba(168,85,253,0.2)" : "none"};

  @media (min-width: 1024px) {
    max-width: 3000px;
  }
`;

// ─── Video Hero ───────────────────────────────────────────────────────────────

const VideoHero = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(10, 0, 16, 0.15) 0%,
      rgba(10, 0, 16, 0.05) 40%,
      rgba(10, 0, 16, 0.55) 80%,
      rgba(10, 0, 16, 1) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

/* Desktop video — hidden on mobile (≤650px) */
const HeroVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;

  @media (max-width: 650px) {
    display: none;
  }

  @media (max-width: 1024px) {
    height: 50vh;
  }
`;

/* Mobile video — shown only on ≤650px */
const HeroVideoMobile = styled.video`
  width: 100%;
  height: auto;
  display: none;
  object-fit: cover;

  @media (max-width: 650px) {
    display: block;
    height: 65vh;
  }
`;

const StoreBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  height: 48px;
  border-radius: 12px;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.15);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    border-color: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 20px;
    height: 20px;
    fill: #fff;
  }
`;

const StoreCopy = styled.div`
  display: flex;
  flex-direction: column;
`;

const StoreTop = styled.span`
  font-size: 0.6rem;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1;
  margin-bottom: 2px;
`;

const StoreBottom = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  line-height: 1;
`;

// ─── Overlay — right-aligned ──────────────────────────────────────────────────

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;

  /* Push content to the right half */
  align-items: flex-end;
  justify-content: center;

  /* Right-side padding; more generous on desktop */
  padding: ${({ theme }) =>
    theme.isDesktop ? "0 10% 0 60px" : "0 28px 0 28px"};
  animation: ${fadeUp} 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;

  /* Constrain the text block width so it doesn't bleed left */
  & > * {
    max-width: ${({ theme }) => (theme.isDesktop ? "480px" : "280px")};
  }

  gap: ${({ theme }) => (theme.isDesktop ? "20px" : "14px")};
`;

const Tagline = styled.p`
  font-size: 5vw;
  font-weight: 100;
  font-style: italic;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.04em;
  text-align: right;
  line-height: 1;
  text-shadow: 0 3px 10px #333;
  font-family: "Voltage", serif;
  max-width: 100%;
  @media (min-width: 1024px) {
    font-size: 5vw;
  }
  @media (max-width: 850px) {
    font-size: 10vw;
  }
`;

const Subtitle = styled.p`
  color: #d1cfcf;
  text-align: right;
  max-width: 580px;
  @media (max-width: 850px) {
    font-size: 14px;
  }
  @media (max-width: 550px) {
    font-size: 10px;
  }
`;

const Cyber = styled.p`
  font-family: "Mendl", serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "14px" : "12px")};
  font-weight: 300;
  font-style: italic;
  color: rgb(211, 211, 211);
  letter-spacing: 0.04em;
  text-align: right;

  span {
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.15s ease;
    &:hover {
      color: rgba(168, 85, 253, 0.9);
    }
  }
  @media (max-width: 550px) {
    font-size: 10px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  /* flex-direction: column; */
  gap: ${({ theme }) => (theme.isDesktop ? "12px" : "10px")};
  align-items: flex-end;
  justify-content: end;
  width: 100%;
  margin-top: ${({ theme }) => (theme.isDesktop ? "4px" : "2px")};
`;

const AppStoreBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  height: 44px;
  border-radius: 10px;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.18);
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const AppleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: -2px;

  svg {
    width: 18px;
    height: 18px;
    fill: #fff;
  }
`;

const AppStoreCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const AppStoreTop = styled.span`
  font-family: -apple-system, "SF Pro Text", "Helvetica Neue", sans-serif;
  font-size: 0.58rem;
  font-weight: 400;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
`;

const AppStoreBottom = styled.span`
  font-family: -apple-system, "SF Pro Display", "Helvetica Neue", sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.1;
  letter-spacing: -0.01em;
  white-space: nowrap;
`;

// ─── Carousel ─────────────────────────────────────────────────────────────────

const CarouselOuter = styled.div`
  position: relative;
  z-index: 5;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  padding: ${({ theme }) => (theme.isDesktop ? "20px 0 40px" : "12px 0 32px")};

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: ${({ theme }) => (theme.isDesktop ? "80px" : "56px")};
    z-index: 20;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(to right, #0a0010, transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(to left, #0a0010, transparent);
  }

  @media (min-width: 1024px) {
    &::before,
    &::after {
      width: 120px;
    }
  }
`;

const CarouselStage = styled.div`
  position: relative;
  width: 100%;
  height: ${({ theme }) => (theme.isDesktop ? "600px" : "360px")};
  min-height: ${({ theme }) => (theme.isDesktop ? "600px" : "280px")};
  display: flex;
  align-items: center;
`;

const BgTrack = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme.isDesktop ? "-10px" : "-10px")};
  display: flex;
  gap: ${({ theme }) => (theme.isDesktop ? "20px" : "14px")};
  width: max-content;
  animation: ${slideTrack} 48s linear infinite reverse;
  will-change: transform;
  z-index: 1;
`;

const BgCard = styled.div`
  width: ${({ theme }) => (theme.isDesktop ? "160px" : "180px")};
  height: ${({ theme }) => (theme.isDesktop ? "252px" : "252px")};
  border-radius: ${({ theme }) => (theme.isDesktop ? "24px" : "18px")};
  flex-shrink: 0;
  background: #1a0533;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: visible;
`;

const BgCardInner = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  opacity: 0.4;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const BgCardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 38%, rgba(0, 0, 0, 0.6) 100%);
  z-index: 1;
`;

const BgCardPill = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme.isDesktop ? "12px" : "9px")};
  z-index: 2;
  height: ${({ theme }) => (theme.isDesktop ? "26px" : "22px")};
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => (theme.isDesktop ? "10px" : "7px")};
  gap: 3px;
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "12px" : "10px")};
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
`;

const BgCardFlag = styled(BgCardPill)`
  left: ${({ theme }) => (theme.isDesktop ? "12px" : "9px")};
  font-size: ${({ theme }) => (theme.isDesktop ? "10px" : "8px")};
  padding: 0 ${({ theme }) => (theme.isDesktop ? "8px" : "6px")};
  background-color: red;
`;

const FlagNotif = styled.div`
  position: absolute;
  top: 4px;
  left: 22px;
  width: ${({ theme }) => (theme.isDesktop ? "18px" : "14px")};
  height: ${({ theme }) => (theme.isDesktop ? "18px" : "14px")};
  border-radius: 50%;
  background: #80413e;
  border: 1.5px solid #795e88;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "9px" : "7.5px")};
  font-weight: 700;
  color: #fff;
  line-height: 1;
  z-index: 10;
`;

const BgCardCount = styled(BgCardPill)`
  right: ${({ theme }) => (theme.isDesktop ? "12px" : "9px")};
  font-size: ${({ theme }) => (theme.isDesktop ? "11px" : "9.5px")};
`;

const BgCardInfo = styled.div`
  position: absolute;
  bottom: ${({ theme }) => (theme.isDesktop ? "14px" : "10px")};
  left: ${({ theme }) => (theme.isDesktop ? "14px" : "10px")};
  z-index: 2;
`;

const BgCardName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "14px" : "12px")};
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.02em;
  line-height: 1.2;
`;

const BgCardAge = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "12px" : "10px")};
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
`;

const FgTrack = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  gap: ${({ theme }) => (theme.isDesktop ? "24px" : "16px")};
  width: max-content;
  animation: ${slideTrack} 56s linear infinite;
  will-change: transform;
  z-index: 3;
  top: ${({ theme }) => (theme.isDesktop ? "70px" : "70px")};
`;

const FgCard = styled.div`
  width: ${({ theme }) => (theme.isDesktop ? "300px" : "250px")};
  height: ${({ theme }) => (theme.isDesktop ? "500px" : "auto")};
  min-width: ${({ theme }) => (theme.isDesktop ? "50px" : "00px")};
  min-height: ${({ theme }) => (theme.isDesktop ? "450px" : "280px")};
  border-radius: 30px;
  flex-shrink: 0;
  overflow: visible;
  position: relative;
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 30px;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.05) 0%,
      transparent 30%,
      transparent 65%,
      rgba(0, 0, 0, 0.28) 100%
    );
    pointer-events: none;
  }
  @media (min-width: 1024px) {
    width: 450px;
  }
`;

const FgCardTitle = styled.div`
  position: absolute;
  top: 60%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  width: 82%;
  text-align: center;
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "48px" : "35px")};
  font-weight: 900;
  color: #fff;
  letter-spacing: 0.06em;
  line-height: 1.15;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
  padding: 5px 0;
  word-wrap: break-word;
  @media (min-width: 1024px) {
    font-size: 56px;
  }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(5, 0, 14, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: ${({ theme }) => (theme.isDesktop ? "center" : "flex-end")};
  justify-content: ${({ theme }) =>
    theme.isDesktop ? "center" : "flex-start"};
  animation: ${backdropIn} 0.28s ease both;
`;

const ModalSheet = styled.div`
  width: 100%;
  max-width: ${({ theme }) => (theme.isDesktop ? "600px" : "100%")};
  max-height: ${({ theme }) => (theme.isDesktop ? "80vh" : "85vh")};
  border-radius: ${({ theme }) => (theme.isDesktop ? "32px" : "28px 28px 0 0")};
  background: linear-gradient(160deg, #ffffff 0%, #ffffff 100%);
  border: 1px solid rgba(168, 85, 253, 0.2);
  box-shadow: 0 -8px 48px rgba(116, 4, 190, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${modalIn} 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  margin: ${({ theme }) => (theme.isDesktop ? "20px" : "0")};
`;

const ModalHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.12);
  margin: 14px auto 0;
  flex-shrink: 0;
  display: ${({ theme }) => (theme.isDesktop ? "none" : "block")};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => (theme.isDesktop ? "20px 24px" : "16px 22px 12px")};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-family: "Mendl", serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "24px" : "22px")};
  font-weight: 400;
  font-style: italic;
  color: #2b2929;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 10px;
  svg {
    color: #a855fd;
    font-size: ${({ theme }) => (theme.isDesktop ? "22px" : "18px")};
    flex-shrink: 0;
  }
`;

const ModalClose = styled.button`
  width: ${({ theme }) => (theme.isDesktop ? "36px" : "32px")};
  height: ${({ theme }) => (theme.isDesktop ? "36px" : "32px")};
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: ${({ theme }) => (theme.isDesktop ? "15px" : "13px")};
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.95);
  }
`;

const ModalScrollBody = styled.div`
  overflow-y: auto;
  padding: ${({ theme }) => (theme.isDesktop ? "0 24px 32px" : "0 22px 36px")};
  flex: 1;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 253, 0.4);
    border-radius: 100px;
  }
`;

const ModalImage = styled.div`
  width: 100%;
  height: ${({ theme }) => (theme.isDesktop ? "220px" : "220px")};
  border-radius: 18px;
  overflow: visible;
  margin-bottom: 100px;
  position: relative;
  video {
    width: 100%;
    height: 300px;
    object-fit: contain;
    display: block;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      160deg,
      rgba(116, 4, 190, 0.3) 0%,
      transparent 60%
    );
    border-radius: 18px;
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 253, 0.3),
    transparent
  );
  margin: 18px 0;
`;

const ModalSection = styled.div`
  margin-bottom: 18px;
`;

const ModalSectionTitle = styled.h3`
  font-family: "Mendl", serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "12px" : "11px")};
  font-weight: 600;
  font-style: italic;
  color: #a855fd;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const ModalBody = styled.p`
  font-family: "Mendl Light", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "14px" : "13.5px")};
  font-weight: 100;
  color: rgba(56, 52, 52, 0.72);
  line-height: 1.75;
  letter-spacing: 0.01em;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────

const glassBase = `
  position: relative;
  width: 100%;
  max-width: 320px;
  height: 56px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  overflow: hidden;
  font-family: 'Mendl', sans-serif;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0.23,1,0.32,1), box-shadow 0.25s cubic-bezier(0.23,1,0.32,1);
  -webkit-tap-highlight-color: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 12%; right: 12%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
    border-radius: 50%;
    z-index: 2;
    pointer-events: none;
  }
  &:active { transform: scale(0.97); }

  @media (min-width: 1024px) { max-width: 360px; height: 64px; font-size: 16px; }
`;

const LiquidGlassPrimary = styled.button`
  ${glassBase}
  background: #f2356e18;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  box-shadow:
    0 0 0 1px rgba(200, 140, 255, 0.45),
    0 1.5px 0 1px rgba(255, 255, 255, 0.18) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.25) inset,
    0 8px 32px rgba(116, 4, 190, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.3);
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  &:hover {
    background: #f2356e;
    transform: translateY(-2px) scale(1.015);
    box-shadow:
      0 0 0 1px rgba(220, 120, 255, 0.7),
      0 1.5px 0 1px rgba(255, 255, 255, 0.22) inset,
      0 -1px 0 1px rgba(0, 0, 0, 0.2) inset,
      0 14px 50px rgba(116, 4, 190, 0.65),
      0 4px 14px rgba(0, 0, 0, 0.35);
  }
`;

const LiquidGlassSecondary = styled.button`
  ${glassBase}
  background: linear-gradient(160deg, rgba(255,255,255,0.18) 0%, rgba(180,180,180,0.10) 40%, rgba(255,255,255,0.06) 100%);
  backdrop-filter: blur(28px) saturate(160%) brightness(1.12);
  -webkit-backdrop-filter: blur(28px) saturate(160%) brightness(1.12);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.28),
    0 1.5px 0 1px rgba(255, 255, 255, 0.22) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.18) inset,
    0 6px 28px rgba(0, 0, 0, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.2);
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  &:hover {
    transform: translateY(-2px) scale(1.015);
    background: linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.26) 0%,
      rgba(200, 200, 200, 0.14) 40%,
      rgba(255, 255, 255, 0.1) 100%
    );
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.42),
      0 1.5px 0 1px rgba(255, 255, 255, 0.28) inset,
      0 -1px 0 1px rgba(0, 0, 0, 0.15) inset,
      0 10px 36px rgba(0, 0, 0, 0.36),
      0 3px 12px rgba(0, 0, 0, 0.25);
  }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────

const PHOTO_IMAGES = [
  { src: b1, title: "Feel the Spark" },
  { src: b4, title: "Real People" },
  { src: b2, title: "Raw Connection" },
  { src: b6, title: "Security First" },
  { src: b5, title: "Discover More" },
  { src: b3, title: "Explore Boldly" },
];

const PROFILE_CARDS = [
  { name: "Amanda", age: 21, flag: "🇮🇹", count: 0, notif: null, img: p4 },
  { name: "Lucas", age: 29, flag: "🇧🇷", count: 12, notif: null, img: p1 },
  { name: "Mia", age: 24, flag: "🇫🇷", count: 9, notif: 1, img: p2 },
  { name: "Richard", age: 31, flag: "🇺🇸", count: 9, notif: 2, img: p3 },
  { name: "Aisha", age: 27, flag: "🇳🇬", count: 5, notif: null, img: p5 },
  { name: "Elena", age: 25, flag: "🇬🇷", count: 2, notif: null, img: p7 },
  { name: "Noah", age: 28, flag: "🇬🇧", count: 14, notif: 2, img: p6 },
];

// ─── Carousel ─────────────────────────────────────────────────────────────────

function LayeredCarousel({ isDesktop }) {
  const bgCards = [...PROFILE_CARDS, ...PROFILE_CARDS, ...PROFILE_CARDS];
  const fgImages = [...PHOTO_IMAGES, ...PHOTO_IMAGES, ...PHOTO_IMAGES];

  return (
    <CarouselStage theme={{ isDesktop }}>
      <BgTrack theme={{ isDesktop }}>
        {bgCards.map((c, i) => (
          <BgCard key={i} theme={{ isDesktop }}>
            <BgCardInner>
              <img src={c.img} alt="" draggable="false" />
              <BgCardOverlay />
              <BgCardFlag theme={{ isDesktop }}>
                <FaFlag />
              </BgCardFlag>
              <BgCardCount theme={{ isDesktop }}>
                {c.count === 0 ? "V" : c.count}
              </BgCardCount>
              <BgCardInfo theme={{ isDesktop }}>
                <BgCardName theme={{ isDesktop }}>{c.name}</BgCardName>
                <BgCardAge theme={{ isDesktop }}>{c.age} years old</BgCardAge>
              </BgCardInfo>
            </BgCardInner>
            {c.notif && <FlagNotif theme={{ isDesktop }}>{c.notif}</FlagNotif>}
          </BgCard>
        ))}
      </BgTrack>

      <FgTrack theme={{ isDesktop }}>
        {fgImages.map((item, i) => (
          <FgCard key={i} theme={{ isDesktop }}>
            <img src={item.src} alt="" draggable="false" />
            <FgCardTitle theme={{ isDesktop }}>{item.title}</FgCardTitle>
          </FgCard>
        ))}
      </FgTrack>
    </CarouselStage>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [showModal, setShowModal] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 0);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const theme = { isDesktop };

  return (
    <>
      <GlobalStyle />
      {/* <Navbar /> */}
      <AppContainer>
        <Phone theme={theme}>
          {/* ── Video Hero with overlaid title + buttons ── */}
          <VideoHero>
            {/* Desktop video (> 650px) */}
            <HeroVideo src={heroVideo} autoPlay muted loop playsInline />
            {/* Mobile video (≤ 650px) */}
            <HeroVideoMobile
              src={heroVideoMobile}
              autoPlay
              muted
              loop
              playsInline
            />

            <VideoOverlay theme={theme}>
              <Tagline theme={theme}>
                Raw Attraction. <br /> Real People.
              </Tagline>
              <Subtitle>
                NudR is the dating app that prioritizes physical attraction and{" "}
                casual encounters, all while keeping your privacy and safety
                intact.
              </Subtitle>
              <Cyber theme={theme}>
                Learn more about our{" "}
                <span onClick={() => setShowModal(true)}>Cybersecurity</span>
              </Cyber>
              <ButtonGroup>
                {/* Apple Store Button */}
                <StoreBtn href="/waitlist">
                  <svg viewBox="0 0 814 1000">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 135.4-317.3 268.9-317.3 70.2 0 128.6 46.4 173 46.4 42.4 0 109.1-49.1 186.3-49.1 30.4 0 110.2 3.9 162.7 69.2zm-85.5-220.5c39.5-46.4 67.8-110.9 67.8-175.4 0-8.9-.6-17.8-2.2-25.4-64.4 2.6-140.5 43.7-185.5 93.8-35.8 39.5-69.7 103.4-69.7 168.5 0 9.5 1.6 19 2.2 22.3 3.9.6 10.4 1.6 16.9 1.6 57.7 0 129.4-39.5 170.5-85.4z" />
                  </svg>
                  <StoreCopy>
                    <StoreTop>Download on the</StoreTop>
                    <StoreBottom>App Store</StoreBottom>
                  </StoreCopy>
                </StoreBtn>

                {/* Google Play Button */}
                <StoreBtn href="/waitlist">
                  <svg viewBox="0 0 512 512">
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                  </svg>
                  <StoreCopy>
                    <StoreTop>Get it on</StoreTop>
                    <StoreBottom>Google Play</StoreBottom>
                  </StoreCopy>
                </StoreBtn>
              </ButtonGroup>
            </VideoOverlay>
          </VideoHero>

          {/* ── Carousels below video ── */}
          <CarouselOuter theme={theme}>
            <LayeredCarousel isDesktop={isDesktop} />
          </CarouselOuter>
        </Phone>
      </AppContainer>

      {/* ── Cybersecurity Modal ── */}
      {showModal && (
        <ModalBackdrop theme={theme} onClick={() => setShowModal(false)}>
          <ModalSheet theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHandle theme={theme} />
            <ModalHeader theme={theme}>
              <ModalTitle theme={theme}>
                <FaShieldAlt />
                Your Security
              </ModalTitle>
              <ModalClose theme={theme} onClick={() => setShowModal(false)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalScrollBody theme={theme}>
              <ModalImage theme={theme}>
                <video src={phVideo} controls></video>
              </ModalImage>

              {[
                {
                  title: "Our Commitment",
                  body: "Your safety is the foundation everything here is built on. We designed our security infrastructure from the ground up with one goal: to make sure every interaction you have on this platform is private, protected, and completely in your control.",
                },
                {
                  title: "End-to-End Encryption",
                  body: "Every message you send is encrypted end-to-end using AES-256 — the same standard used by financial institutions and governments worldwide. Not even our own servers can read your conversations. Your words belong only to you and the person you're speaking with.",
                },
                {
                  title: "Identity Verification",
                  body: "Every profile goes through a real-time identity verification process before being activated. We use document scanning combined with liveness detection to ensure the person behind the screen is exactly who they say they are. No bots. No fake profiles. No exceptions.",
                },
                {
                  title: "Data Privacy",
                  body: "We never sell your data — to anyone, ever. Your personal information, location, and activity patterns are never shared with third-party advertisers. You can request a full export or permanent deletion of your data at any time, and we will process it within 48 hours, no questions asked.",
                },
                {
                  title: "Photo & Media Protection",
                  body: "All photos and media you share are stored in encrypted containers and are never indexed or accessible outside of the app. Screenshots within private chats are detected and the other party is notified immediately. Your images cannot be downloaded or saved without your explicit permission.",
                },
                {
                  title: "Location Safety",
                  body: "We never expose your precise GPS coordinates. When location is enabled, we show only a generalised proximity — enough to help you find people nearby, never enough for anyone to track or locate you. You can disable location entirely at any time from your settings.",
                },
                {
                  title: "Reporting & Moderation",
                  body: "Our moderation team operates 24/7. Any profile or message can be reported with a single tap, and our team reviews every report within minutes. Repeated violations result in permanent bans and, where applicable, reports to the relevant authorities. We take abuse seriously — always.",
                },
              ].map((s, i, arr) => (
                <div key={i}>
                  <ModalSection>
                    <ModalSectionTitle theme={theme}>
                      {s.title}
                    </ModalSectionTitle>
                    <ModalBody theme={theme}>{s.body}</ModalBody>
                  </ModalSection>
                  {i < arr.length - 1 && <ModalDivider />}
                </div>
              ))}
            </ModalScrollBody>
          </ModalSheet>
        </ModalBackdrop>
      )}
    </>
  );
}
